// src/pages/Upload.js
import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, getDocs, query, where, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useDataContext } from '../contexts/DataContext';

function Upload() {
  const [file, setFile] = useState(null);
  const [schoolYear, setSchoolYear] = useState('');
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [processingInfo, setProcessingInfo] = useState('');
  const { refreshData } = useDataContext();
  
  // File preview and mapping state
  const [previewData, setPreviewData] = useState(null);
  const [mapping, setMapping] = useState({
    studentId: '',
    studentName: '',
    courseName: '',
    courseLength: '',
    earnedCredits: '',
    gradYear: ''  // Added grad year field
  });
  const [showMapping, setShowMapping] = useState(false);
  const [requiredFields] = useState([
    { id: 'studentId', label: 'Student ID', required: true },
    { id: 'studentName', label: 'Student Name', required: true },
    { id: 'courseName', label: 'Course Name', required: true },
    { id: 'courseLength', label: 'Course Length', required: false },
    { id: 'earnedCredits', label: 'Earned Credits', required: true },
    { id: 'gradYear', label: 'Graduation Year', required: false }
  ]);

  // Available school years
  const schoolYears = [
    '19-20', '20-21', '21-22', '22-23', '23-24', '24-25', '25-26', '26-27'
  ];

  // Fetch existing uploads
  useEffect(() => {
    fetchUploads();
  }, []);

  const fetchUploads = async () => {
    try {
      const uploadsSnapshot = await getDocs(collection(db, 'uploads'));
      const uploadsList = uploadsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      
      // Sort by timestamp
      uploadsList.sort((a, b) => b.timestamp - a.timestamp);
      
      setUploads(uploadsList);
    } catch (error) {
      console.error("Error fetching uploads: ", error);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSchoolYearChange = (e) => {
    setSchoolYear(e.target.value);
  };

  const handleDeleteUpload = async (uploadId) => {
    if (!window.confirm("Are you sure? This will delete all student data from this upload.")) {
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      updateProcessingInfo(`Deleting upload and related student records...`);
      
      // Find all student records from this upload
      const studentQuery = query(
        collection(db, 'students'), 
        where('uploadId', '==', uploadId)
      );
      
      const studentDocs = await getDocs(studentQuery);
      updateProcessingInfo(`Found ${studentDocs.size} student records to delete`);
      
      // Delete all student records in a batch
      const batch = writeBatch(db);
      
      studentDocs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Delete the upload record
      batch.delete(doc(db, 'uploads', uploadId));
      
      // Commit the batch
      await batch.commit();
      updateProcessingInfo(`Deleted upload and ${studentDocs.size} student records`);
      
      // Refresh the uploads list
      await fetchUploads();
      
      // Signal other components to refresh
      refreshData();
      
      setSuccess(`Successfully deleted upload and ${studentDocs.size} student records`);
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error("Error deleting upload:", error);
      setError(`Failed to delete upload: ${error.message}`);
    } finally {
      setUploading(false);
      setProcessingInfo('');
    }
  };

  const handlePreviewFile = async () => {
    if (!file || !schoolYear) {
      setError("Please select a file and school year");
      return;
    }
    
    setError(null);
    
    try {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const fileData = event.target.result;
        let headers = [];
        let sampleData = [];
        
        try {
          if (file.name.toLowerCase().endsWith('.csv')) {
            // Parse CSV with Papa Parse
            Papa.parse(fileData, {
              header: true,
              preview: 5,
              skipEmptyLines: true,
              complete: (results) => {
                headers = results.meta.fields || [];
                sampleData = results.data.slice(0, 5);
                setPreviewData({ headers, sampleData });
                setShowMapping(true);
                
                // Auto-map columns if names are similar
                const newMapping = {...mapping};
                requiredFields.forEach(field => {
                  const matchingHeader = headers.find(header => 
                    header?.toLowerCase().includes(field.id.toLowerCase()) ||
                    field.label.toLowerCase().includes(header?.toLowerCase())
                  );
                  if (matchingHeader) {
                    newMapping[field.id] = matchingHeader;
                  }
                });
                setMapping(newMapping);
              }
            });
          } else {
            // Parse Excel file
            const workbook = XLSX.read(fileData, { type: 'binary' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (jsonData.length > 0) {
              headers = jsonData[0];
              sampleData = jsonData.slice(1, 6).map(row => {
                const rowData = {};
                headers.forEach((header, index) => {
                  rowData[header] = row[index];
                });
                return rowData;
              });
              
              setPreviewData({ headers, sampleData });
              setShowMapping(true);
              
              // Auto-map columns if names are similar
              const newMapping = {...mapping};
              requiredFields.forEach(field => {
                const matchingHeader = headers.find(header => 
                  header && typeof header === 'string' && (
                    header.toLowerCase().includes(field.id.toLowerCase()) ||
                    field.label.toLowerCase().includes(header.toLowerCase())
                  )
                );
                if (matchingHeader) {
                  newMapping[field.id] = matchingHeader;
                }
              });
              setMapping(newMapping);
            }
          }
        } catch (error) {
          console.error("Error previewing file:", error);
          setError("Failed to preview file: " + error.message);
        }
      };
      
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        setError("Error reading file");
      };
      
      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    } catch (err) {
      console.error("Preview error:", err);
      setError(err.message || "Failed to preview file");
    }
  };

  const processSpreadsheet = async (file, schoolYear, fieldMapping) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          const fileData = event.target.result;
          let processedData = [];
          
          updateProcessingInfo(`Processing ${file.name} for school year ${schoolYear}...`);
          
          // Check file type and parse accordingly
          if (file.name.toLowerCase().endsWith('.csv')) {
            // Parse CSV with Papa Parse
            Papa.parse(fileData, {
              header: true,
              skipEmptyLines: true,
              complete: async (results) => {
                try {
                  updateProcessingInfo(`CSV parsed successfully with ${results.data.length} rows`);
                  
                  // Apply field mapping
                  processedData = results.data.map(row => {
                    const mappedRow = {};
                    // Map fields according to our configuration
                    if (fieldMapping.studentId) mappedRow['Student ID'] = row[fieldMapping.studentId];
                    if (fieldMapping.studentName) mappedRow['Student Name'] = row[fieldMapping.studentName];
                    if (fieldMapping.courseName) mappedRow['Course Name'] = row[fieldMapping.courseName];
                    if (fieldMapping.courseLength) mappedRow['Course Length'] = row[fieldMapping.courseLength];
                    if (fieldMapping.earnedCredits) mappedRow['Earned Credits'] = row[fieldMapping.earnedCredits];
                    if (fieldMapping.gradYear) mappedRow['Grad Year'] = row[fieldMapping.gradYear];
                    return mappedRow;
                  });
                  
                  // Continue with processing...
                  const uploadId = await continueProcessing(processedData, file, schoolYear);
                  resolve(uploadId);
                } catch (error) {
                  console.error("Error processing CSV data:", error);
                  reject(error);
                }
              },
              error: (error) => {
                console.error("Error parsing CSV:", error);
                reject(error);
              }
            });
          } else {
            // Parse Excel file
            try {
              const workbook = XLSX.read(fileData, { type: 'binary' });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              
              // Convert to JSON
              const jsonData = XLSX.utils.sheet_to_json(worksheet);
              updateProcessingInfo(`Excel file parsed successfully with ${jsonData.length} rows`);
              
              // Apply field mapping
              processedData = jsonData.map(row => {
                const mappedRow = {};
                // Map fields according to our configuration
                if (fieldMapping.studentId) mappedRow['Student ID'] = row[fieldMapping.studentId];
                if (fieldMapping.studentName) mappedRow['Student Name'] = row[fieldMapping.studentName];
                if (fieldMapping.courseName) mappedRow['Course Name'] = row[fieldMapping.courseName];
                if (fieldMapping.courseLength) mappedRow['Course Length'] = row[fieldMapping.courseLength];
                if (fieldMapping.earnedCredits) mappedRow['Earned Credits'] = row[fieldMapping.earnedCredits];
                if (fieldMapping.gradYear) mappedRow['Grad Year'] = row[fieldMapping.gradYear];
                return mappedRow;
              });
              
              const uploadId = await continueProcessing(processedData, file, schoolYear);
              resolve(uploadId);
            } catch (xlsxError) {
              console.error("Error processing Excel file:", xlsxError);
              reject(xlsxError);
            }
          }
        } catch (error) {
          console.error("File processing error:", error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        reject(error);
      };
      
      // Read the file based on type
      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  const updateProcessingInfo = (info) => {
    setProcessingInfo(prev => `${prev}\n${info}`);
    console.log(info);
  };

  // Continue processing after file parsing - skips storage upload
  const continueProcessing = async (processedData, file, schoolYear) => {
    try {
      updateProcessingInfo(`Processing student data from ${processedData.length} records...`);
      
      // Add metadata to the data vault
      const uploadRef = await addDoc(collection(db, 'uploads'), {
        fileName: file.name,
        schoolYear: schoolYear,
        timestamp: serverTimestamp(),
        recordCount: processedData.length
      });
      
      const uploadId = uploadRef.id;
      updateProcessingInfo(`Created upload record with ID: ${uploadId}`);
      
      // Store the processed data in the vault
      const studentCourses = processStudentData(processedData);
      const studentCount = Object.keys(studentCourses).length;
      
      updateProcessingInfo(`Found ${studentCount} unique students, adding to database...`);
      
      // Add batches of student data
      let successCount = 0;
      let totalStudents = Object.keys(studentCourses).length;
      
      // Use batch for better performance
      const batchSize = 250; // Firestore batch limit is 500, we use 250 to be safe
      let batch = writeBatch(db);
      let operationsCount = 0;
      
      for (const [index, studentId] of Object.keys(studentCourses).entries()) {
        try {
          const studentRef = doc(collection(db, 'students'));
          batch.set(studentRef, {
            studentId: studentId,
            studentName: studentCourses[studentId].name,
            courses: studentCourses[studentId].courses,
            schoolYear: schoolYear,
            uploadId: uploadId, // Link to the upload
            gradYear: studentCourses[studentId].gradYear || null,
            timestamp: serverTimestamp()
          });
          
          operationsCount++;
          
          // If we've reached batch limit, commit and create a new batch
          if (operationsCount >= batchSize) {
            await batch.commit();
            batch = writeBatch(db);
            operationsCount = 0;
          }
          
          successCount++;
          
          // Update progress
          if (index % 5 === 0 || index === totalStudents - 1) {
            const currentProgress = Math.round((successCount / totalStudents) * 100);
            setProgress(currentProgress);
            updateProcessingInfo(`Processed ${successCount}/${totalStudents} students (${currentProgress}%)`);
          }
        } catch (studentError) {
          console.error(`Error adding student ${studentId}:`, studentError);
        }
      }
      
      // Commit any remaining operations
      if (operationsCount > 0) {
        await batch.commit();
      }
      
      updateProcessingInfo(`Successfully processed and stored ${successCount} students for ${schoolYear} school year`);
      return uploadId;
    } catch (error) {
      console.error(`Error in continueProcessing:`, error);
      throw error;
    }
  };

  // Process student data from file
  const processStudentData = (data) => {
    const students = {};
    
    // Log the first row to see what fields are available
    if (data.length > 0) {
      console.log("Sample row fields:", Object.keys(data[0]));
    }
    
    data.forEach(row => {
      // Check for required fields
      if (!row['Student ID'] || !row['Student Name']) {
        console.warn("Skipping row due to missing Student ID or Student Name:", row);
        return;
      }
      
      const studentId = row['Student ID'];
      
      if (!students[studentId]) {
        students[studentId] = {
          name: row['Student Name'],
          courses: [],
          gradYear: row['Grad Year'] || null
        };
      }
      
      // Ensure Course Name exists
      if (!row['Course Name']) {
        console.warn(`Missing Course Name for student ${studentId}`);
        return;
      }
      
      // Add a unique identifier to each course instance to ensure proper credit counting
      students[studentId].courses.push({
        courseName: row['Course Name'],
        courseLength: row['Course Length'] || '',
        earnedCredits: parseFloat(row['Earned Credits']) || 0,
        courseInstanceId: `${row['Course Name']}_${Math.random().toString(36).substr(2, 9)}` // Add unique ID
      });
    });
    
    return students;
  };

  const handleMappingChange = (fieldId, headerName) => {
    setMapping({
      ...mapping,
      [fieldId]: headerName
    });
  };

  const handleConfirmMapping = async () => {
    // Validate that required fields are mapped
    const missingRequiredFields = requiredFields
      .filter(field => field.required && !mapping[field.id])
      .map(field => field.label);
    
    if (missingRequiredFields.length > 0) {
      setError(`Please map required fields: ${missingRequiredFields.join(', ')}`);
      return;
    }
    
    setShowMapping(false);
    setUploading(true);
    setError(null);
    setSuccess(false);
    setProcessingInfo('Starting upload process...');
    
    try {
      const uploadId = await processSpreadsheet(file, schoolYear, mapping);
      setSuccess(`File uploaded successfully! Upload ID: ${uploadId}`);
      
      // Refresh uploads list
      await fetchUploads();
      
      // Signal other components to refresh - but only AFTER the upload is complete
      refreshData();
      
      // Reset form
      setFile(null);
      setSchoolYear('');
      setProgress(0);
      setProcessingInfo('');
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Unknown error occurred during upload");
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!file || !schoolYear) {
      setError("Please select a file and school year");
      return;
    }
    
    handlePreviewFile();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Upload Student Records</h1>
      
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}
      
      {/* Field Mapping Modal */}
      {showMapping && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Map Spreadsheet Columns</h2>
            <p className="mb-4">Please map the columns from your spreadsheet to the required fields:</p>
            
            <div className="mb-4">
              <h3 className="font-semibold mb-2">Sample Data Preview:</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      {previewData.headers.map((header, index) => (
                        <th key={index} className="border px-4 py-2 text-left">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.sampleData.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {previewData.headers.map((header, colIndex) => (
                          <td key={colIndex} className="border px-4 py-2">
                            {row[header]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-semibold mb-2">Map Fields:</h3>
              {requiredFields.map(field => (
                <div key={field.id} className="mb-4">
                  <label className="block mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <select 
                    value={mapping[field.id] || ''}
                    onChange={(e) => handleMappingChange(field.id, e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required={field.required}
                  >
                    <option value="">-- Select a column --</option>
                    {previewData.headers.map((header, index) => (
                      <option key={index} value={header}>{header}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowMapping(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmMapping}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Confirm & Upload
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upload Form */}
        <div className="border rounded p-6 bg-white">
          <h2 className="text-xl font-semibold mb-4">Upload New File</h2>
          
          <form onSubmit={handleUpload}>
            <div className="mb-4">
              <label className="block mb-2 font-semibold">School Year</label>
              <select
                value={schoolYear}
                onChange={handleSchoolYearChange}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option value="">-- Select School Year --</option>
                {schoolYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block mb-2 font-semibold">Student Data Spreadsheet</label>
              <input
                type="file"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleFileChange}
                className="block w-full border border-gray-300 rounded px-3 py-2"
                required
              />
            </div>
            
            {uploading && (
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-blue-600 h-4 rounded-full"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-center mt-2">Uploading and processing file... {progress}%</p>
                
                {processingInfo && (
                  <div className="mt-2 p-2 bg-gray-50 border rounded text-sm h-32 overflow-y-auto">
                    {processingInfo.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <button
              type="submit"
              disabled={uploading || !file || !schoolYear}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {uploading ? 'Processing...' : 'Start Upload Process'}
            </button>
          </form>
        </div>
        
        {/* Upload History */}
        <div className="border rounded p-6 bg-white">
          <h2 className="text-xl font-semibold mb-4">Upload History</h2>
          
          {uploads.length === 0 ? (
            <p className="text-gray-500">No uploads yet. Upload your first file to get started.</p>
          ) : (
            <div className="overflow-y-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School Year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {uploads.map(upload => (
                    <tr key={upload.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">{upload.fileName}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{upload.schoolYear}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{formatDate(upload.timestamp)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{upload.recordCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleDeleteUpload(upload.id)}
                          className="text-red-600 hover:text-red-900"
                          disabled={uploading}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Upload;